import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
    STORAGE_KEYS,
    safeGetItem,
    safeRemoveItem,
    safeSetItem,
} from "../lib/clientStorage";

const ACTIVE_POOL_KEY = STORAGE_KEYS.ACTIVE_POOL;

export function usePools() {
    const { activeOrg, orgReady } = useAuth();
    const [pools, setPools] = useState([]);
    const [activePoolId, setActivePoolId] = useState(() => safeGetItem(ACTIVE_POOL_KEY));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const orgIdRef = useRef(activeOrg?.id);

    useEffect(() => {
        if (activeOrg?.id !== orgIdRef.current) {
            orgIdRef.current = activeOrg?.id;
            setPools([]);
            setActivePoolId(null);
            safeRemoveItem(ACTIVE_POOL_KEY);
            setLoading(true);
        }
    }, [activeOrg?.id]);

    const refetch = useCallback(async () => {
        if (!orgReady || !activeOrg?.id) {
            setPools([]);
            setLoading(false);
            return;
        }
        const isBackground = pools.length > 0;
        if (!isBackground) setLoading(true);
        try {
            const { data } = await api.get("/pools", { params: { org_id: activeOrg.id } });
            const list = (data.pools || []).map((p) => ({
                ...p,
                members: p.members || [],
                total_revenue: p.total_revenue ?? 0,
                name: p.name || "Unnamed pool",
            }));
            setPools(list);
            const ids = list.map((p) => p.id);
            setActivePoolId((current) => {
                if (current && ids.includes(current)) return current;
                const next = ids[0] || null;
                if (next) safeSetItem(ACTIVE_POOL_KEY, next);
                else safeRemoveItem(ACTIVE_POOL_KEY);
                return next;
            });
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, [activeOrg?.id, orgReady, pools.length]);

    useEffect(() => { refetch(); }, [refetch]);

    const activePool = pools.find((p) => p.id === activePoolId) || pools[0] || null;

    const selectPool = (id) => {
        setActivePoolId(id);
        safeSetItem(ACTIVE_POOL_KEY, id);
    };

    const updateActivePool = (next) => {
        setPools((all) => all.map((p) => (p.id === next.id ? next : p)));
    };

    const removePool = useCallback((poolId) => {
        setPools((all) => {
            const nextList = all.filter((p) => p.id !== poolId);
            setActivePoolId((current) => {
                if (current !== poolId) return current;
                const nextId = nextList[0]?.id || null;
                if (nextId) safeSetItem(ACTIVE_POOL_KEY, nextId);
                else safeRemoveItem(ACTIVE_POOL_KEY);
                return nextId;
            });
            return nextList;
        });
    }, []);

    return { pools, activePool, activePoolId, selectPool, loading, error, refetch, updateActivePool, removePool };
}
